import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { notesSchema, type NotesForm } from '../schemas';
import type { ModerationActions } from '../hooks/useModerationActions';

/**
 * Internal notes (doc 09 §4.10). Save is enabled only for a non-blank note; the field clears on
 * success and the backend auto-creates a note-history entry, so the timeline reloads via invalidate.
 */
export function InternalNotesCard({ actions }: { actions: ModerationActions }) {
  const form = useForm<NotesForm>({
    resolver: zodResolver(notesSchema),
    defaultValues: { notes: '' },
    mode: 'onChange',
  });

  const onSubmit = async (values: NotesForm) => {
    await actions.saveNote(values.notes);
    form.reset();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Internal Notes</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-3">
          <Textarea rows={4} placeholder="Add an internal note…" {...form.register('notes')} />
          {form.formState.errors.notes && (
            <p className="text-xs text-destructive">{form.formState.errors.notes.message}</p>
          )}
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={!form.formState.isValid || actions.isPending}>
              Save Note
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
